import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MondialiSectionPageShell, useIsInsideMondialiShell } from './MondialiHomePage';
import FaIcon from './FaIcon';
import {
  MONDIALI_PREMI_PATH,
  getMondialiWeekWindows,
  isMondialiFinaleAvailable,
} from '../utils/mondialiRouting';
import {
  getMondialiFinaleLeaderboard,
  getMondialiLeaderboardForRange,
  getMondialiTopPlayerLeaderboard,
} from '../api/mondiali';
import {
  buildDemoFinalePayload,
  buildDemoRangeLeaderboardPayload,
  buildDemoTopPlayerLeaderboardPayload,
} from '../utils/mondialiPreviewData';
import styles from '../styles/Mondiali.module.css';

const PARTNERS = {
  alma: {
    name: 'ALMA Edizioni',
    url: 'https://www.almaedizioni.it/',
  },
  teacherStefano: {
    name: 'Teacher Stefano',
    url: 'https://www.teacherstefano.com/',
  },
  vaporetto: {
    name: 'Vaporetto Italiano',
    url: 'https://vaporettoitaliano.com/',
  },
  italianLanguageHub: {
    name: 'Italian Language Hub',
    url: 'https://www.italianlanguagehub.com/',
  },
  stivale: {
    name: 'Stivale Italiano',
    url: 'https://www.stivaleitaliano.com/',
  },
  onlineItalianClasses: {
    name: 'Online Italian Classes',
    url: 'https://onlineitalianclasses.com/',
  },
};

const withUtmSource = (url) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set('utm_source', 'giochini-di-parole');
    return parsedUrl.toString();
  } catch (_) {
    return url;
  }
};

const WEEKLY_PRIZES = [
  {
    weekNumber: 1,
    dateLabel: '14 giugno - 20 giugno',
    first: { what: 'Bundle di 3 ebook', icon: 'fa-book-open', category: 'Ebook', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
    second: { what: 'Corso "15 minute Italian coffee break"', icon: 'fa-graduation-cap', category: 'Corso', partnerKey: 'italianLanguageHub', prizeUrl: 'https://www.italianlanguagehub.com/15-minute-Italian-Coffee-Break' },
    third: { what: 'Abbonamento Patreon "Esploratore Esperto"', icon: 'fa-key', category: 'Abbonamento', detail: 'Durata: 1 mese', partnerKey: 'stivale', prizeUrl: 'https://www.stivaleitaliano.com/il-circolo' },
  },
  {
    weekNumber: 2,
    dateLabel: '21 giugno - 27 giugno',
    first: { what: '1 corso B1/B2/C1 a scelta', icon: 'fa-graduation-cap', category: 'Corso', partnerKey: 'vaporetto', prizeUrl: 'https://vaporettoitaliano.teachable.com/l/products?sortKey=recommended&sortDirection=asc&page=1' },
    second: { what: 'Bundle di 2 ebook', icon: 'fa-book-open', category: 'Ebook', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
    third: { what: 'Abbonamento Substack a "Every Week Italian"', icon: 'fa-envelope-open-text', category: 'Abbonamento', detail: 'Durata: 4 settimane', partnerKey: 'onlineItalianClasses', prizeUrl: 'https://onlineitalianclasses.com/product/every-week-italian-membership/' },
  },
  {
    weekNumber: 3,
    dateLabel: '28 giugno - 4 luglio',
    first: { what: 'Bundle di 2 corsi "Italiano Vero" a scelta', icon: 'fa-graduation-cap', category: 'Corso', partnerKey: 'teacherStefano', prizeUrl: 'https://school.teacherstefano.com/courses' },
    second: { what: '1 ingresso al club di conversazione', icon: 'fa-users', category: 'Conversazione', partnerKey: 'stivale', prizeUrl: 'https://www.stivaleitaliano.com/corsi' },
    third: { what: 'Ebook "Giro d\'Italia con un clic"', icon: 'fa-book-open', category: 'Ebook', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
  },
  {
    weekNumber: 4,
    dateLabel: '5 luglio - 11 luglio',
    first: { what: 'Bundle di 3 ebook', icon: 'fa-book-open', category: 'Ebook', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
    second: { what: 'Corso "The Sound of Coffee"', icon: 'fa-graduation-cap', category: 'Corso', partnerKey: 'italianLanguageHub', prizeUrl: 'https://www.italianlanguagehub.com/the-sound-of-coffee-espresso' },
    third: { what: 'Abbonamento Substack a "Every Week Italian"', icon: 'fa-envelope-open-text', category: 'Abbonamento', detail: 'Durata: 4 settimane', partnerKey: 'onlineItalianClasses', prizeUrl: 'https://onlineitalianclasses.com/product/every-week-italian-membership/' },
  },
  {
    weekNumber: 5,
    dateLabel: '12 luglio - 18 luglio',
    first: { what: '1 corso "Italiano Vero" a scelta', icon: 'fa-graduation-cap', category: 'Corso', partnerKey: 'teacherStefano', prizeUrl: 'https://school.teacherstefano.com/courses' },
    second: { what: 'Bundle di 2 ebook', icon: 'fa-book-open', category: 'Ebook', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
    third: { what: 'Abbonamento Patreon "Cercatore"', icon: 'fa-key', category: 'Abbonamento', detail: 'Durata: 1 mese', partnerKey: 'stivale', prizeUrl: 'https://www.stivaleitaliano.com/il-circolo' },
  },
];

const FINAL_PRIZES = [
  {
    place: '1° posto',
    prizes: [
      { what: '100€ Coupon ALMA + merchandising', icon: 'fa-tag', partnerKey: 'alma', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
      { what: 'Bundle di 3 corsi "Italiano Vero"', icon: 'fa-graduation-cap', partnerKey: 'teacherStefano', prizeUrl: 'https://school.teacherstefano.com/courses' },
    ],
  },
  {
    place: '2° posto',
    prizes: [
      { what: '70€ Coupon ALMA + merchandising', icon: 'fa-tag', partnerKey: 'alma', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
      { what: '1 corso B1/B2/C1 a scelta', icon: 'fa-graduation-cap', partnerKey: 'vaporetto', prizeUrl: 'https://vaporettoitaliano.teachable.com/l/products?sortKey=recommended&sortDirection=asc&page=1' },
    ],
  },
  {
    place: '3° posto',
    prizes: [
      { what: '50€ Coupon ALMA + merchandising', icon: 'fa-tag', partnerKey: 'alma', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
      { what: 'Sconto 20% su lezioni 1:1 online', icon: 'fa-tag', detail: 'Valido per 1 anno', partnerKey: 'onlineItalianClasses', prizeUrl: 'https://onlineitalianclasses.com/one-to-one-lessons/' },
    ],
  },
];

const EXTRA_PRIZES = [
  {
    key: 'top-player',
    title: 'Top Player',
    description: 'I primi 3 giocatori con piu stelline in assoluto durante i Mondiali.',
    awards: [
      { place: '1° posto', what: '1 corso B1/B2/C1 a scelta', icon: 'fa-graduation-cap', partnerKey: 'vaporetto', prizeUrl: 'https://vaporettoitaliano.teachable.com/l/products?sortKey=recommended&sortDirection=asc&page=1' },
      { place: '2° posto', what: 'Bundle di 2 ebook', icon: 'fa-book-open', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
      { place: '3° posto', what: 'Membership "LeggiAmo"', icon: 'fa-newspaper', partnerKey: 'italianLanguageHub', prizeUrl: 'https://www.italianlanguagehub.com/leggiamo' },
    ],
  },
  {
    key: 'stacanovisti',
    title: 'Stacanovisti',
    description: 'I primi 3 giocatori della squadra vincitrice che hanno partecipato ogni giorno alla competizione.',
    awards: [
      { place: 'Premio x3', what: 'Bundle di 3 ebook', icon: 'fa-book-open', partnerKey: 'alma', detail: 'Durata: 14 mesi', prizeUrl: 'https://www.almaedizioni.it/catalogo/' },
    ],
  },
];

const TOP_PLAYER_PRIZE_GROUP = EXTRA_PRIZES.find((group) => group.key === 'top-player') || null;
const MONDIALI_WEEK_WINDOWS = getMondialiWeekWindows();
const MONDIALI_COMPETITION_START_AT = new Date(MONDIALI_WEEK_WINDOWS[0].startTimestamp).toISOString();
const MONDIALI_COMPETITION_END_AT = new Date(MONDIALI_WEEK_WINDOWS[MONDIALI_WEEK_WINDOWS.length - 1].endTimestamp + 1000).toISOString();

const WEEKLY_PODIUM = [
  { key: 'first', label: '1° Premio' },
  { key: 'second', label: '2° Premio' },
  { key: 'third', label: '3° Premio' },
];

const findAwardByPlaceNumber = (awards, placeNumber) =>
  awards.find((award) => award.place.startsWith(`${placeNumber}°`)) || null;

const buildRepeatedAwards = (awards, total = 3) => {
  if (!awards || awards.length === 0) {
    return [];
  }

  const [baseAward] = awards;
  return Array.from({ length: total }, (_, index) => ({
    ...baseAward,
    slot: index + 1,
  }));
};

const getWeekRangePayload = (weekNumber) => {
  const selectedWeek = MONDIALI_WEEK_WINDOWS.find((week) => week.weekNumber === weekNumber);
  if (!selectedWeek) {
    return null;
  }

  return {
    startAt: new Date(selectedWeek.startTimestamp).toISOString(),
    endAt: new Date(selectedWeek.endTimestamp + 1000).toISOString(),
  };
};

const createEmptyPrizeWinnerState = () => ({
  weeklyPodiumsByWeek: {},
  finalePodium: null,
  topPlayerPodium: null,
  stakanovistiPodium: null,
});

const getEndedPrizeWeeks = (effectiveNow) => MONDIALI_WEEK_WINDOWS.filter(
  (week) => effectiveNow.getTime() >= week.endTimestamp + 1000,
);

const loadPrizeWinnerState = async ({ effectiveNow, isDemoMode = false }) => {
  const endedWeeks = getEndedPrizeWeeks(effectiveNow);
  const shouldLoadFinaleWinners = isMondialiFinaleAvailable(effectiveNow);
  const nextState = createEmptyPrizeWinnerState();
  let hadPartialError = false;

  if (endedWeeks.length > 0) {
    if (isDemoMode) {
      endedWeeks.forEach((week) => {
        const rangePayload = getWeekRangePayload(week.weekNumber);
        if (!rangePayload) {
          return;
        }

        nextState.weeklyPodiumsByWeek[week.weekNumber] = buildDemoRangeLeaderboardPayload({
          ...rangePayload,
          now: effectiveNow,
        }).podium || null;
      });
    } else {
      const weeklyResponses = await Promise.all(
        endedWeeks.map(async (week) => {
          const rangePayload = getWeekRangePayload(week.weekNumber);
          if (!rangePayload) {
            return [week.weekNumber, null];
          }

          try {
            const response = await getMondialiLeaderboardForRange(rangePayload);
            return [week.weekNumber, response?.podium || null];
          } catch (_) {
            hadPartialError = true;
            return [week.weekNumber, null];
          }
        }),
      );

      weeklyResponses.forEach(([weekNumber, podium]) => {
        nextState.weeklyPodiumsByWeek[weekNumber] = podium;
      });
    }
  }

  if (shouldLoadFinaleWinners) {
    if (isDemoMode) {
      const finalePayload = buildDemoFinalePayload({
        startAt: MONDIALI_COMPETITION_START_AT,
        endAt: MONDIALI_COMPETITION_END_AT,
        now: effectiveNow,
      });
      const topPlayerPayload = buildDemoTopPlayerLeaderboardPayload({
        startAt: MONDIALI_COMPETITION_START_AT,
        endAt: MONDIALI_COMPETITION_END_AT,
        now: effectiveNow,
      });
      nextState.finalePodium = finalePayload.podium || null;
      nextState.stakanovistiPodium = finalePayload.stacanovisti_podium || null;
      nextState.topPlayerPodium = topPlayerPayload.podium || null;
    } else {
      const [finaleResult, topPlayerResult] = await Promise.allSettled([
        getMondialiFinaleLeaderboard(),
        getMondialiTopPlayerLeaderboard(),
      ]);

      if (finaleResult.status === 'fulfilled') {
        nextState.finalePodium = finaleResult.value?.podium || null;
        nextState.stakanovistiPodium = finaleResult.value?.stacanovisti_podium || null;
      } else {
        hadPartialError = true;
      }

      if (topPlayerResult.status === 'fulfilled') {
        nextState.topPlayerPodium = topPlayerResult.value?.podium || null;
      } else {
        hadPartialError = true;
      }
    }
  }

  return {
    ...nextState,
    hadPartialError,
  };
};

const PartnerLabel = ({ partnerKey }) => {
  const partner = PARTNERS[partnerKey];
  if (!partner) {
    return null;
  }

  return <span className={styles.prizePartnerName}>{partner.name}</span>;
};

const getPrizeLinkUrl = (prize) => {
  if (!prize) {
    return null;
  }

  if (prize.prizeUrl) {
    return withUtmSource(prize.prizeUrl);
  }

  const partnerUrl = PARTNERS[prize.partnerKey]?.url;
  return partnerUrl ? withUtmSource(partnerUrl) : null;
};

const PrizeCell = ({ prize }) => {
  if (!prize) {
    return null;
  }

  const linkUrl = getPrizeLinkUrl(prize);

  const prizeItem = (
    <>
      <div className={styles.prizeIconBadge} aria-hidden="true">
        <FaIcon className={`fa ${prize?.icon || 'fa-gift'} ${styles.prizeIcon}`} />
      </div>
      <div className={styles.prizeMeta}>
        <span className={styles.prizeTitle}>{prize.what}</span>
        {prize.detail ? <span className={styles.prizeDetail}>{prize.detail}</span> : null}
        <PartnerLabel partnerKey={prize.partnerKey} />
      </div>
    </>
  );

  return (
    <div className={styles.prizeCell}>
      {linkUrl ? (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.prizeCardLink}
        >
          {prizeItem}
        </a>
      ) : (
        <div className={styles.prizeItem}>
          {prizeItem}
        </div>
      )}
    </div>
  );
};

const FlagUsernameLabel = ({ winner, labelClassName = '' }) => (
  <span className={styles.leaderboardCountryLabel}>
    <span className={styles.flagFrame} aria-hidden="true">
      <span className={`fi fi-${winner.country_alpha2.toLowerCase()} ${styles.flag}`} />
    </span>
    <span className={labelClassName}>{winner.username}</span>
  </span>
);

const PodiumWinnerContent = ({ winner }) => (
  <span className={styles.weeklyPodiumBaseWinner}>
    <FlagUsernameLabel winner={winner} labelClassName={styles.weeklyPodiumBaseWinnerName} />
  </span>
);

const WeeklyPodiumTier = ({
  label,
  prize,
  standClassName = '',
  placeNumber,
  baseContent = null,
  baseClassName = '',
  baseIsDecorative = true,
}) => (
  <article className={`${styles.weeklyPodiumTier} ${standClassName}`} aria-label={label}>
    <div className={styles.weeklyPodiumStand}>
      <div className={styles.weeklyPodiumMedalWrap} aria-hidden="true">
        <span className={styles.weeklyPodiumRibbon} />
        <span className={styles.weeklyPodiumMedalBadge}>{placeNumber}</span>
      </div>
      <PrizeCell prize={prize} />
    </div>
    <div className={`${styles.weeklyPodiumBase} ${baseClassName}`} aria-hidden={baseIsDecorative ? 'true' : undefined}>
      {baseContent || placeNumber}
    </div>
  </article>
);

const FinalPodiumTier = ({
  placeNumber,
  prizes,
  standClassName = '',
  label,
  baseContent = null,
  baseClassName = '',
  baseIsDecorative = true,
}) => (
  <article className={`${styles.weeklyPodiumTier} ${styles.finalPodiumTier} ${standClassName}`} aria-label={label}>
    <div className={styles.weeklyPodiumStand}>
      <div className={styles.weeklyPodiumMedalWrap} aria-hidden="true">
        <span className={styles.weeklyPodiumRibbon} />
        <span className={styles.weeklyPodiumMedalBadge}>{placeNumber}</span>
      </div>
      <ul className={styles.finalPodiumPrizeList}>
        {prizes.map((prize) => (
          <li key={`final-${placeNumber}-${prize.what}`} className={styles.finalPodiumPrizeItem}>
            <PrizeCell prize={prize} />
          </li>
        ))}
      </ul>
    </div>
    <div className={`${styles.weeklyPodiumBase} ${baseClassName}`} aria-hidden={baseIsDecorative ? 'true' : undefined}>
      {baseContent || placeNumber}
    </div>
  </article>
);

const StakanovistiTier = ({
  prize,
  slot,
  label = null,
  baseContent = null,
  baseClassName = '',
  baseIsDecorative = true,
}) => (
  <article className={`${styles.weeklyPodiumTier} ${styles.stakanovistiTier}`} aria-label={label || `Vincitore ${slot}`}>
    <div className={styles.weeklyPodiumStand}>
      <div className={styles.weeklyPodiumMedalWrap} aria-hidden="true">
        <span className={styles.weeklyPodiumRibbon} />
        <span className={styles.weeklyPodiumMedalBadge}>★</span>
      </div>
      <PrizeCell prize={prize} />
    </div>
    <div className={`${styles.weeklyPodiumBase} ${baseClassName}`} aria-hidden={baseIsDecorative ? 'true' : undefined}>
      {baseContent || '★'}
    </div>
  </article>
);

const MondialiPremiContent = ({
  weeklyPodiumsByWeek,
  finalePodium,
  topPlayerPodium,
  stakanovistiPodium,
  errorMessage = '',
}) => {
  const topPlayerGroup = TOP_PLAYER_PRIZE_GROUP;
  const stakanovistiGroup = EXTRA_PRIZES.find((group) => group.key === 'stacanovisti');
  const stakanovistiAwards = buildRepeatedAwards(stakanovistiGroup?.awards, 3);

  const weeklyWinnerMapsByWeek = useMemo(
    () => Object.fromEntries(
      Object.entries(weeklyPodiumsByWeek).map(([weekNumber, podium]) => [
        Number(weekNumber),
        new Map((podium || []).map((winner) => [Number(winner.place), winner])),
      ]),
    ),
    [weeklyPodiumsByWeek],
  );
  const finaleWinnerByPlace = useMemo(
    () => new Map((finalePodium || []).map((winner) => [Number(winner.place), winner])),
    [finalePodium],
  );
  const topPlayerWinnerByPlace = useMemo(
    () => new Map((topPlayerPodium || []).map((winner) => [Number(winner.place), winner])),
    [topPlayerPodium],
  );
  const stakanovistiWinnerByPlace = useMemo(
    () => new Map((stakanovistiPodium || []).map((winner) => [Number(winner.place), winner])),
    [stakanovistiPodium],
  );

  const content = (
    <>
      <section className={styles.prizesCard} aria-labelledby="premi-page-title">
        <h2 id="premi-page-title" className={styles.prizesCardTitle}>
          <FaIcon className={`fa fa-trophy ${styles.prizesTitleIcon}`} aria-hidden="true" />
          <span>Premi Mondiali</span>
        </h2>
        {errorMessage ? <p className={styles.errorBanner}>{errorMessage}</p> : null}
        <p className={styles.prizesCardText}>
          Ogni settimana vincono 3 giocatori: il miglior giocatore della prima, della seconda e della terza squadra classificata. Oltre ai premi settimanali, ci sono premi finali e premi extra. I vincitori verranno contattati personalmente via mail per la riscossione dei premi.
        </p>

        <section className={styles.prizesSubsection} aria-labelledby="premi-settimanali-title">
          <h3 id="premi-settimanali-title" className={styles.prizesSubsectionTitle}>
            <FaIcon className={`fa fa-calendar-days ${styles.prizesTitleIcon}`} aria-hidden="true" />
            <span>Premi Settimanali</span>
          </h3>
          <div className={styles.weeklyPodiumGrid}>
            {WEEKLY_PRIZES.map((week) => {
              const winnerByPlace = weeklyWinnerMapsByWeek[week.weekNumber] || new Map();

              return (
                <article key={`week-${week.weekNumber}`} className={styles.weeklyPodiumWeek}>
                  <h4 className={styles.weeklyPodiumWeekHeader}>
                    <span className={styles.weeklyPodiumWeekTitle}>{week.weekNumber}° Settimana</span>
                    <span className={styles.prizesWeekDatePill}>{week.dateLabel}</span>
                  </h4>
                  <div className={styles.weeklyOlympicPodium}>
                    <WeeklyPodiumTier
                      label={WEEKLY_PODIUM[1].label}
                      prize={week.second}
                      standClassName={styles.weeklyPodiumTierSecond}
                      placeNumber={2}
                      baseContent={winnerByPlace.has(2) ? <PodiumWinnerContent winner={winnerByPlace.get(2)} /> : null}
                      baseClassName={winnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
                      baseIsDecorative={!winnerByPlace.has(2)}
                    />
                    <WeeklyPodiumTier
                      label={WEEKLY_PODIUM[0].label}
                      prize={week.first}
                      standClassName={styles.weeklyPodiumTierFirst}
                      placeNumber={1}
                      baseContent={winnerByPlace.has(1) ? <PodiumWinnerContent winner={winnerByPlace.get(1)} /> : null}
                      baseClassName={winnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
                      baseIsDecorative={!winnerByPlace.has(1)}
                    />
                    <WeeklyPodiumTier
                      label={WEEKLY_PODIUM[2].label}
                      prize={week.third}
                      standClassName={styles.weeklyPodiumTierThird}
                      placeNumber={3}
                      baseContent={winnerByPlace.has(3) ? <PodiumWinnerContent winner={winnerByPlace.get(3)} /> : null}
                      baseClassName={winnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
                      baseIsDecorative={!winnerByPlace.has(3)}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.prizesSubsection} aria-labelledby="premi-finali-title">
          <h3 id="premi-finali-title" className={styles.prizesSubsectionTitle}>
            <FaIcon className={`fa fa-star ${styles.prizesTitleIcon}`} aria-hidden="true" />
            <span>Premi Finali</span>
          </h3>
          <div className={`${styles.weeklyOlympicPodium} ${styles.finalOlympicPodium}`}>
            <FinalPodiumTier
              placeNumber={2}
              label="2° posto finale"
              prizes={FINAL_PRIZES[1].prizes}
              standClassName={styles.weeklyPodiumTierSecond}
              baseContent={finaleWinnerByPlace.has(2) ? <PodiumWinnerContent winner={finaleWinnerByPlace.get(2)} /> : null}
              baseClassName={finaleWinnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
              baseIsDecorative={!finaleWinnerByPlace.has(2)}
            />
            <FinalPodiumTier
              placeNumber={1}
              label="1° posto finale"
              prizes={FINAL_PRIZES[0].prizes}
              standClassName={styles.weeklyPodiumTierFirst}
              baseContent={finaleWinnerByPlace.has(1) ? <PodiumWinnerContent winner={finaleWinnerByPlace.get(1)} /> : null}
              baseClassName={finaleWinnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
              baseIsDecorative={!finaleWinnerByPlace.has(1)}
            />
            <FinalPodiumTier
              placeNumber={3}
              label="3° posto finale"
              prizes={FINAL_PRIZES[2].prizes}
              standClassName={styles.weeklyPodiumTierThird}
              baseContent={finaleWinnerByPlace.has(3) ? <PodiumWinnerContent winner={finaleWinnerByPlace.get(3)} /> : null}
              baseClassName={finaleWinnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
              baseIsDecorative={!finaleWinnerByPlace.has(3)}
            />
          </div>
        </section>

        <section className={styles.prizesSubsection} aria-labelledby="premi-top-player-title">
          <h3 id="premi-top-player-title" className={styles.prizesSubsectionTitle}>
            <FaIcon className={`fa fa-bolt ${styles.prizesTitleIcon}`} aria-hidden="true" />
            <span>Top Player</span>
          </h3>
          {topPlayerGroup ? (
            <>
              <p className={styles.prizesExtraDescription}>{topPlayerGroup.description}</p>
              <div className={`${styles.weeklyOlympicPodium} ${styles.extraTopPlayersPodium}`}>
                <WeeklyPodiumTier
                  label="2° posto top player"
                  prize={findAwardByPlaceNumber(topPlayerGroup.awards, 2)}
                  standClassName={styles.weeklyPodiumTierSecond}
                  placeNumber={2}
                  baseContent={topPlayerWinnerByPlace.has(2) ? <PodiumWinnerContent winner={topPlayerWinnerByPlace.get(2)} /> : null}
                  baseClassName={topPlayerWinnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
                  baseIsDecorative={!topPlayerWinnerByPlace.has(2)}
                />
                <WeeklyPodiumTier
                  label="1° posto top player"
                  prize={findAwardByPlaceNumber(topPlayerGroup.awards, 1)}
                  standClassName={styles.weeklyPodiumTierFirst}
                  placeNumber={1}
                  baseContent={topPlayerWinnerByPlace.has(1) ? <PodiumWinnerContent winner={topPlayerWinnerByPlace.get(1)} /> : null}
                  baseClassName={topPlayerWinnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
                  baseIsDecorative={!topPlayerWinnerByPlace.has(1)}
                />
                <WeeklyPodiumTier
                  label="3° posto top player"
                  prize={findAwardByPlaceNumber(topPlayerGroup.awards, 3)}
                  standClassName={styles.weeklyPodiumTierThird}
                  placeNumber={3}
                  baseContent={topPlayerWinnerByPlace.has(3) ? <PodiumWinnerContent winner={topPlayerWinnerByPlace.get(3)} /> : null}
                  baseClassName={topPlayerWinnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
                  baseIsDecorative={!topPlayerWinnerByPlace.has(3)}
                />
              </div>
            </>
          ) : null}
        </section>

        <section className={styles.prizesSubsection} aria-labelledby="premi-stacanovisti-title">
          <h3 id="premi-stacanovisti-title" className={styles.prizesSubsectionTitle}>
            <FaIcon className={`fa fa-bolt ${styles.prizesTitleIcon}`} aria-hidden="true" />
            <span>Stacanovisti</span>
          </h3>
          {stakanovistiGroup ? (
            <>
              <p className={styles.prizesExtraDescription}>{stakanovistiGroup.description}</p>
              <div className={`${styles.weeklyOlympicPodium} ${styles.stakanovistiTrio}`}>
                {stakanovistiAwards.map((award) => (
                  <StakanovistiTier
                    key={`stakanovisti-slot-${award.slot}`}
                    prize={award}
                    slot={award.slot}
                    baseContent={stakanovistiWinnerByPlace.has(award.slot) ? <PodiumWinnerContent winner={stakanovistiWinnerByPlace.get(award.slot)} /> : null}
                    baseClassName={stakanovistiWinnerByPlace.has(award.slot) ? styles.weeklyPodiumBaseHasWinner : ''}
                    baseIsDecorative={!stakanovistiWinnerByPlace.has(award.slot)}
                  />
                ))}
              </div>
            </>
          ) : null}
        </section>
      </section>
    </>
  );

  return content;
};

const MondialiPremiPage = () => {
  const isInsideMondialiShell = useIsInsideMondialiShell();
  const [now] = useState(() => new Date());
  const [prizeWinnerState, setPrizeWinnerState] = useState(() => createEmptyPrizeWinnerState());

  useEffect(() => {
    let cancelled = false;

    const loadPrizeWinners = async () => {
      const nextState = await loadPrizeWinnerState({ effectiveNow: now });
      if (!cancelled) {
        setPrizeWinnerState({
          weeklyPodiumsByWeek: nextState.weeklyPodiumsByWeek,
          finalePodium: nextState.finalePodium,
          topPlayerPodium: nextState.topPlayerPodium,
          stakanovistiPodium: nextState.stakanovistiPodium,
        });
      }
    };

    loadPrizeWinners();

    return () => {
      cancelled = true;
    };
  }, [now]);

  const content = (
    <MondialiPremiContent
      weeklyPodiumsByWeek={prizeWinnerState.weeklyPodiumsByWeek}
      finalePodium={prizeWinnerState.finalePodium}
      topPlayerPodium={prizeWinnerState.topPlayerPodium}
      stakanovistiPodium={prizeWinnerState.stakanovistiPodium}
    />
  );

  if (isInsideMondialiShell) {
    return content;
  }

  return (
    <MondialiSectionPageShell
      pageTitle="Premi Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={MONDIALI_PREMI_PATH}
      now={now}
    >
      {content}
    </MondialiSectionPageShell>
  );
};

const MondialiPremiPreviewPage = () => {
  const now = new Date();
  const location = useLocation();
  const [prizeWinnerState, setPrizeWinnerState] = useState(() => createEmptyPrizeWinnerState());
  const [errorMessage, setErrorMessage] = useState('');

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startAtParam = useMemo(() => queryParams.get('start_at') || '', [queryParams]);
  const endAtParam = useMemo(() => queryParams.get('end_at') || '', [queryParams]);
  const isDemoMode = useMemo(() => queryParams.get('demo') === '1', [queryParams]);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      setErrorMessage('');
      setPrizeWinnerState(createEmptyPrizeWinnerState());

      if (!startAtParam || !endAtParam) {
        setErrorMessage('Specifica entrambi i parametri query start_at e end_at in formato ISO-8601.');
        return;
      }

      const startDate = new Date(startAtParam);
      const endDate = new Date(endAtParam);
      const isStartInvalid = Number.isNaN(startDate.getTime());
      const isEndInvalid = Number.isNaN(endDate.getTime());
      if (isStartInvalid || isEndInvalid) {
        setErrorMessage('Parametri non validi: usa date ISO-8601 per start_at e end_at.');
        return;
      }

      if (startDate >= endDate) {
        setErrorMessage('Intervallo non valido: start_at deve essere precedente a end_at.');
        return;
      }

      const nextState = await loadPrizeWinnerState({
        effectiveNow: endDate,
        isDemoMode,
      });

      if (!cancelled) {
        setPrizeWinnerState({
          weeklyPodiumsByWeek: nextState.weeklyPodiumsByWeek,
          finalePodium: nextState.finalePodium,
          topPlayerPodium: nextState.topPlayerPodium,
          stakanovistiPodium: nextState.stakanovistiPodium,
        });
        if (nextState.hadPartialError) {
          setErrorMessage("Impossibile caricare completamente l'anteprima premi. Alcuni podi potrebbero essere incompleti.");
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [endAtParam, isDemoMode, startAtParam]);

  return (
    <MondialiSectionPageShell
      pageTitle="Anteprima Premi Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={null}
      now={now}
    >
      <MondialiPremiContent
        weeklyPodiumsByWeek={prizeWinnerState.weeklyPodiumsByWeek}
        finalePodium={prizeWinnerState.finalePodium}
        topPlayerPodium={prizeWinnerState.topPlayerPodium}
        stakanovistiPodium={prizeWinnerState.stakanovistiPodium}
        errorMessage={errorMessage}
      />
    </MondialiSectionPageShell>
  );
};

export default MondialiPremiPage;
export {
  WEEKLY_PRIZES,
  FINAL_PRIZES,
  EXTRA_PRIZES,
  TOP_PLAYER_PRIZE_GROUP,
  findAwardByPlaceNumber,
  WeeklyPodiumTier,
  FinalPodiumTier,
  StakanovistiTier,
  MondialiPremiPreviewPage,
};
